import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Compass, Moon, Sun, LogOut, Bell, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { useSettingsStore } from "../../store/settingsStore";
import { useProfile } from "../../hooks/useProfile";
import { useDebounce } from "../../utils/debounce";
import { useAuth } from "../../contexts/AuthContext";
import { authService } from "../../services/auth";
import LanguageSelector from "../LanguageSelector";
import { NotificationBadge } from "../NotificationsCenter/NotificationBadge";
import { NotificationList } from "../NotificationsCenter/NotificationList";
import { useNotifications } from "../NotificationsCenter/NotificationContext";
import { NotificationHistory } from "../../lib/api/notificationApi";

// Types
type ProfileUpdates = {
  darkMode?: boolean;
  language?: string;
  notifications?: {
    enabled?: boolean | object;
    [key: string]: any;
  };
  [key: string]: any;
};

interface NavActionButtonProps {
  onClick: () => void;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
  ariaLabel?: string;
}

export function Navbar(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { darkMode, language, notifications, setLanguage } = useSettingsStore();
  const { user, setUser } = useAuth();
  const { updateProfile, isLoading: isUpdating } = useProfile();
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState<boolean>(false);
  const [localDarkMode, setLocalDarkMode] = useState<boolean>(darkMode);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState<boolean>(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const notificationsRef = useRef<HTMLDivElement | null>(null);
  const mobileMenuRef = useRef<HTMLDivElement | null>(null);

  // Usar el contexto de notificaciones actualizado
  const {
    hasUnread,
    unreadCount,
    refreshNotifications,
    isPushSupported,
    loading: notificationsLoading
  } = useNotifications();

  const refreshNotificationsRef = useRef(refreshNotifications);

  // Update the ref when refreshNotifications changes
  useEffect(() => {
    refreshNotificationsRef.current = refreshNotifications;
  }, [refreshNotifications]);

  const debouncedUpdate = useDebounce(async (updates: ProfileUpdates): Promise<void> => {
    try {
      const cleanUpdates: ProfileUpdates = { ...updates };

      if ('notifications' in cleanUpdates && cleanUpdates.notifications) {
        const notificationsCopy = { ...cleanUpdates.notifications };

        if (typeof notificationsCopy.enabled === 'object') {
          notificationsCopy.enabled = true;
        }

        cleanUpdates.notifications = notificationsCopy;
      }

      await updateProfile(cleanUpdates);
    } catch (err) {
      if ("darkMode" in updates) setLocalDarkMode(darkMode);
      if ("language" in updates) setLanguage(language);
      console.error("Failed to update settings:", err);
    }
  }, 1000);

  useEffect(() => {
    const handleScroll = (): void => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (localDarkMode !== darkMode) {
      debouncedUpdate({ darkMode: localDarkMode, language, notifications });
    }
  }, [localDarkMode, darkMode, language, notifications, debouncedUpdate]);

  useEffect(() => {
    setLocalDarkMode(darkMode);
  }, [darkMode]);

  // Close notifications menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      // Only close notifications if clicking outside both the notifications dropdown AND the bell icon
      if (
        isNotificationsOpen &&
        notificationsRef.current &&
        !notificationsRef.current.contains(event.target as Node)
      ) {
        setIsNotificationsOpen(false);
      }

      if (
        isMobileMenuOpen &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node)
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isNotificationsOpen, isMobileMenuOpen]);

  // Update notifications when opening dropdown
  // Fixed by using the ref to avoid dependency on refreshNotifications
  useEffect(() => {
    if (isNotificationsOpen) {
      // Use the ref's current value instead of the refreshNotifications prop
      refreshNotificationsRef.current();
      console.log("Cargando notificaciones en el dropdown");
    }
  }, [isNotificationsOpen]); // Removed refreshNotifications from dependencies

  const handleDarkModeToggle = (): void => {
    // Toggle the dark mode
    const newMode = !localDarkMode;
    setLocalDarkMode(newMode);

    // Ensure we don't accidentally close menus when toggling
    // by stopping the event propagation in the NavActionButton
    console.log("Dark mode toggled:", newMode);
  };

  const handleLanguageChange = async (langCode: string): Promise<void> => {
    setIsLanguageMenuOpen(false);
    setLanguage(langCode);
    debouncedUpdate({ darkMode, language: langCode, notifications });
  };

  const handleLogout = async (): Promise<void> => {
    if (isLoggingOut) return;
    try {
      setIsLoggingOut(true);
      await authService.logout();
      setUser(null);
    } catch (error) {
      console.error("Error logging out:", error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const toggleNotifications = (): void => {
    setIsNotificationsOpen(!isNotificationsOpen);
  };

  const toggleMobileMenu = (): void => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Actualizado para usar NotificationHistory
  const handleNotificationClick = (notification: NotificationHistory): void => {
    setIsNotificationsOpen(false);

    // Procesar la navegación basada en los datos de la notificación
    if (notification.data) {
      // Priorizar ruta específica de objetivo
      if (notification.data.goalId) {
        navigate(`/app/goals/${notification.data.goalId}`);
        return;
      }

      // Procesar ruta genérica
      if (notification.data.route) {
        navigate(notification.data.route);
        return;
      }

      if (notification.data.url) {
        // Verificar si es URL externa
        if (notification.data.url.startsWith('http')) {
          window.open(notification.data.url, '_blank');
        } else {
          navigate(notification.data.url);
        }
        return;
      }
    }

    // Comportamiento predeterminado: ir a la página de detalles de notificación
    navigate(`/notifications/${notification._id}`);
  };

  // Component for nav action button
  const NavActionButton = React.memo<NavActionButtonProps>(({
    onClick,
    disabled = false,
    className = "",
    children,
    ariaLabel
  }) => (
    <motion.button
      onClick={(e) => {
        // Prevent event bubbling to avoid unexpected menu closures
        e.stopPropagation();
        onClick();
      }}
      disabled={disabled}
      className={`relative p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 ${className}`}
      aria-label={ariaLabel}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.98 }}
    >
      {children}
    </motion.button>
  ));

  // Dark Mode Button Component with Memoization
  const DarkModeButton = React.memo<{
    darkMode: boolean;
    isUpdating: boolean;
    onToggle: () => void;
    ariaLabel: string;
  }>(({ darkMode, isUpdating, onToggle, ariaLabel }) => (
    <NavActionButton
      onClick={onToggle}
      disabled={isUpdating}
      ariaLabel={ariaLabel}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={darkMode ? 'dark' : 'light'}
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.5, opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          {darkMode ? (
            <Moon className="w-5 h-5 text-blue-600" />
          ) : (
            <Sun className="w-5 h-5 text-yellow-500" />
          )}
        </motion.div>
      </AnimatePresence>
      {isUpdating && (
        <span className="absolute -top-1 -right-1 flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
        </span>
      )}
    </NavActionButton>
  ));

  // Verificar si las notificaciones están disponibles
  const notificationsAvailable = true

  return (
    <motion.nav
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-200 ${isScrolled
        ? 'bg-white/95 dark:bg-gray-900/95 shadow-sm backdrop-blur-sm'
        : 'bg-white dark:bg-gray-900'
        }`}
    >
      <div className="w-full px-4 mx-auto max-w-7xl">
        <div className="flex items-center justify-between h-16">
          {/* Logo & Title */}
          <Link
            to="/"
            className="flex items-center space-x-2"
          >
            <motion.div
              whileHover={{ rotate: 90 }}
              transition={{ duration: 0.2 }}
              className="relative"
            >
              <Compass className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              <div className="absolute inset-0 bg-blue-400/20 rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity" />
            </motion.div>
            <span className="text-lg font-semibold text-gray-900 dark:text-white">
              {t("navbar.title")}
            </span>
          </Link>

          {/* Desktop Actions Container */}
          <div className="hidden md:flex items-center space-x-2">
            {/* Notifications - Desktop */}
            {notificationsAvailable && (
              <div className="relative" ref={notificationsRef}>
                <NavActionButton
                  onClick={toggleNotifications}
                  ariaLabel={t("notifications.title")}
                >
                  <Bell className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  {hasUnread && (
                    <NotificationBadge className="absolute -top-1 -right-1" />
                  )}
                </NavActionButton>

                {/* Notifications dropdown */}
                <AnimatePresence>
                  {isNotificationsOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 5, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 overflow-hidden"
                    >
                      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                        <h3 className="font-medium text-gray-900 dark:text-white">{t("notifications.title")}</h3>
                        <Link
                          to="/notifications"
                          className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                          onClick={() => setIsNotificationsOpen(false)}
                        >
                          {t("notifications.viewAll")}
                        </Link>
                      </div>

                      <div style={{ maxHeight: "400px" }} className="overflow-y-auto">
                        <NotificationList
                          limit={5}
                          onNotificationClick={handleNotificationClick}
                          screen="navbar-dropdown"
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Language Selector - Desktop */}
            <div className="relative">
              <LanguageSelector
                variant="dropdown"
                currentLanguage={language}
                onLanguageChange={handleLanguageChange}
                disabled={isUpdating}
              />
            </div>

            {/* Dark Mode Toggle - Desktop */}
            <DarkModeButton
              darkMode={localDarkMode}
              isUpdating={isUpdating}
              onToggle={handleDarkModeToggle}
              ariaLabel={localDarkMode ? t("settings.darkMode") : t("settings.lightMode")}
            />

            {/* Logout Button - Desktop */}
            <NavActionButton
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10"
              ariaLabel={isLoggingOut ? t("navbar.loggingOut") : t("navbar.logout")}
            >
              <LogOut className="w-5 h-5" />
              <span className="sr-only">{isLoggingOut ? t("navbar.loggingOut") : t("navbar.logout")}</span>
            </NavActionButton>
          </div>

          {/* Mobile Actions */}
          <div className="md:hidden flex items-center space-x-1">
            {/* Dark Mode Toggle - Mobile */}
            <DarkModeButton
              darkMode={localDarkMode}
              isUpdating={isUpdating}
              onToggle={handleDarkModeToggle}
              ariaLabel={localDarkMode ? t("settings.darkMode") : t("settings.lightMode")}
            />

            {/* Notifications Icon - Mobile */}
            {notificationsAvailable && (
              <div className="relative" ref={notificationsRef}>
                <NavActionButton
                  onClick={toggleNotifications}
                  ariaLabel={t("notifications.title")}
                >
                  <Bell className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  {hasUnread && (
                    <NotificationBadge className="absolute -top-1 -right-1" />
                  )}
                </NavActionButton>

                {/* Notification Dropdown - Mobile */}
                <AnimatePresence>
                  {isNotificationsOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 5, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 overflow-hidden"
                      style={{ width: "280px", right: "-115px" }}
                    >
                      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                        <h3 className="font-medium text-gray-900 dark:text-white">{t("notifications.title")}</h3>
                        <Link
                          to="/notifications"
                          className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                          onClick={() => setIsNotificationsOpen(false)}
                        >
                          {t("notifications.viewAll")}
                        </Link>
                      </div>

                      <div style={{ maxHeight: "350px" }} className="overflow-y-auto">
                        <NotificationList
                          limit={4}
                          onNotificationClick={handleNotificationClick}
                          screen="navbar-mobile"
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Mobile Menu Button */}
            <NavActionButton
              onClick={toggleMobileMenu}
              ariaLabel={isMobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-600 dark:text-gray-300" />
              ) : (
                <Menu className="w-6 h-6 text-gray-600 dark:text-gray-300" />
              )}
            </NavActionButton>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            ref={mobileMenuRef}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700"
          >
            <div className="px-4 py-3 space-y-3">
              {/* Language Selector - Mobile */}
              <div className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
                <LanguageSelector
                  variant="mobile"
                  currentLanguage={language}
                  onLanguageChange={(langCode: string) => {
                    handleLanguageChange(langCode);
                    setIsMobileMenuOpen(false);
                  }}
                  disabled={isUpdating}
                />
              </div>

              {/* Logout Button - Mobile */}
              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="flex items-center space-x-3 w-full p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-lg transition-colors disabled:opacity-50"
              >
                <LogOut className="w-5 h-5" />
                <span className="text-sm font-medium">
                  {isLoggingOut ? t("navbar.loggingOut") : t("navbar.logout")}
                </span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}

export default Navbar;